import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  ButtonV3Component,
  CheckboxHeaderRendererComponent,
  CheckboxRendererComponent,
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  ComplexDialogV3Service,
  HeaderRendererComponentV3,
  TableV3Component,
} from '@gea/digital-ui-lib';
import { ActivatedRoute } from '@angular/router';
import { UserManagementService } from '../../../../services/user-management.service';
import { Grant, PermissionsMetadata, UserDTO, UserGrantRow } from '../../user-management.model';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UtilityService } from '../../../../shared/services/utility.service';
import { AddGrantsComponent } from '../../dialogs/add-grants/add-grants.component';
import { DeleteGrantsDialogComponent } from '../../dialogs/delete-grants/delete-grants-dialog.component';

@Component({
  selector: 'app-grants-tab',
  imports: [TableV3Component, ButtonV3Component],
  templateUrl: './grants-tab.component.html',
  styleUrl: './grants-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrantsTabComponent implements OnInit {
  userId = '';
  grants = signal<UserGrantRow[]>([]);
  selectedGrants = signal<UserGrantRow[]>([]);
  loadingGrants = signal(false);
  fetchingMetadata = signal(false);
  tableId = 'grantsTable';
  grantsColumnDefinition: ColumnDefinitionV3[] = [
    {
      displayName: '',
      key: 'checkboxCol',
      minWidth: 40,
      width: 40,
      sortable: false,
      headerRenderer: {
        component: CheckboxHeaderRendererComponent as HeaderRendererComponentV3<unknown>,
      },
      renderer: {
        component: CheckboxRendererComponent as ColumnRendererComponentV3<unknown>,
      },
    },
    {
      displayName: 'Grants',
      key: 'grant',
      minWidth: 200,
    },
    {
      displayName: 'Target',
      key: 'target',
      minWidth: 200,
    },
  ];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly activeRoute: ActivatedRoute,
    private readonly userManagementService: UserManagementService,
    private readonly dialogService: ComplexDialogV3Service,
    private readonly utilityService: UtilityService
  ) {}

  ngOnInit(): void {
    this.userId = this.activeRoute.parent?.snapshot.paramMap.get('id')!;
    this.fetchUserGrants();
  }

  fetchUserGrants(): void {
    this.loadingGrants.set(true);
    this.userManagementService
      .getUserDetails(this.userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingGrants.set(false))
      )
      .subscribe({
        next: (response: UserDTO) => {
          this.grants.set(this.userManagementService.mapGrantsToArray(response.permissions.grants));
        },
      });
  }

  addGrants(): void {
    this.fetchingMetadata.set(true);
    this.userManagementService
      .getMetadata(this.userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.fetchingMetadata.set(false))
      )
      .subscribe({
        next: (metadata: PermissionsMetadata) => {
          const { ref } = this.dialogService.open(
            {
              header: 'Add Grants',
              styleClass: 'add-grants-dialog',
              hideButtons: true,
              dismissableMask: true,
              data: {
                grants: this.getGrantNames(metadata.grants),
                userId: this.userId,
              },
            },
            AddGrantsComponent
          );

          ref.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((success: boolean) => {
            if (success && !this.utilityService.isExitEventFromGEADialog(success)) {
              this.fetchUserGrants();
            }
          });
        },
      });
  }

  getGrantNames(grants: Grant[]): string[] {
    return [...new Set(grants.map((g) => g.type))];
  }
  onSelectionChange(selectedRows: UserGrantRow[]): void {
    this.selectedGrants.set(selectedRows);
  }

  deleteSelectedGrants(): void {
    if (this.selectedGrants().length === 0) return;

    const { ref } = this.dialogService.open(
      {
        header: 'Delete Grants',
        styleClass: 'delete-grants-dialog',
        hideButtons: true,
        dismissableMask: true,
        data: {
          userId: this.userId,
          grants: this.selectedGrants(),
          grantCount: this.selectedGrants().length,
        },
      },
      DeleteGrantsDialogComponent
    );

    ref.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((success: boolean) => {
      if (success && !this.utilityService.isExitEventFromGEADialog(success)) {
        this.selectedGrants.set([]);
        this.fetchUserGrants();
      }
    });
  }
}
